import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Alert } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/Info';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import './ChipsPaginationWrapper.scss';

/**
 * A reusable wrapper component that provides pagination controls and loading overlay
 * for chip grid displays with unified view (selected items are visually marked).
 * 
 * Used for paginated user and organization selection in modals.
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Chip elements to render (all items, selected ones marked visually)
 * @param {number} props.currentPage - Current page number (0-indexed)
 * @param {number} props.totalCount - Total number of items from API
 * @param {number} props.pageSize - Number of items per page (e.g., 50 for users, 30 for orgs)
 * @param {boolean} props.isLoading - Whether page data is currently loading
 * @param {Function} props.onPageChange - Callback when page changes: (newPage) => void
 * @param {number} [props.selectedCount=0] - Count of selected items (optional display)
 * @param {string} [props.selectedLabel=''] - i18n label for selected items (e.g., "selected")
 * @param {string} [props.className] - Additional CSS class name
 * @param {string} [props.emptyMessage] - Message to display when no items available
 * @returns {JSX.Element} Wrapper component with pagination controls
 * 
 * @example
 * <ChipsPaginationWrapper
 *   currentPage={0}
 *   totalCount={150}
 *   pageSize={50}
 *   isLoading={false}
 *   onPageChange={(page) => setPage(page)}
 *   selectedCount={5}
 * >
 *   {chips}
 * </ChipsPaginationWrapper>
 */
export const ChipsPaginationWrapper = ({
    children,
    currentPage,
    totalCount,
    pageSize,
    isLoading,
    onPageChange,
    selectedCount = 0,
    selectedLabel = '',
    className = '',
    emptyMessage = ''
}) => {
    // Calculate total pages based on totalCount and pageSize
    const totalPages = useMemo(() => {
        if (pageSize <= 0) {
            console.warn('ChipsPaginationWrapper: pageSize must be positive, got:', pageSize);
            return 1;
        }
        return Math.max(1, Math.ceil(totalCount / pageSize));
    }, [totalCount, pageSize]);

    // Check if navigation is possible
    const hasPrevPage = currentPage > 0;
    const hasNextPage = currentPage < totalPages - 1;

    /**
     * Handles previous page navigation
     * @returns {void}
     */
    const handlePrevPage = () => {
        if (hasPrevPage && !isLoading) {
            onPageChange(currentPage - 1);
        }
    };

    /**
     * Handles next page navigation
     * @returns {void}
     */
    const handleNextPage = () => {
        if (hasNextPage && !isLoading) {
            onPageChange(currentPage + 1);
        }
    };

    // Check if children array is empty
    const hasChildren = React.Children.count(children) > 0;

    return (
        <div className={`chipsPaginationWrapper ${className}`}>

            {/* Chips Container with Loading Overlay */}
            <div className={`chipsContainer ${isLoading ? 'isLoading' : ''}`}>
                {isLoading && (
                    <div className="loadingOverlay">
                        <LoadingSpinner local />
                    </div>
                )}
                <div 
                    className="chipsContent" 
                    aria-busy={isLoading}
                >
                    {hasChildren ? children : (
                        <div className="emptyMessage">
                            {emptyMessage}
                        </div>
                    )}
                </div>
            </div>

            {/* Pagination Controls - Show when there are multiple pages */}
            {totalPages > 1 && (
                <div className="paginationControls">
                    <div className="navigationGroup">
                        <IconButton
                            onClick={handlePrevPage}
                            disabled={!hasPrevPage || isLoading}
                            className={`paginationArrow ${(!hasPrevPage || isLoading) ? 'disabled' : ''}`}
                            aria-label="previous page"
                            size="small"
                        >
                            <ChevronLeftIcon />
                        </IconButton>

                        <span className="pageIndicator">
                            {currentPage + 1} / {totalPages}
                        </span>

                        <IconButton
                            onClick={handleNextPage}
                            disabled={!hasNextPage || isLoading}
                            className={`paginationArrow ${(!hasNextPage || isLoading) ? 'disabled' : ''}`}
                            aria-label="next page"
                            size="small"
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </div>
                    {/* Selected count indicator */}
                    {selectedCount > 0 && selectedLabel && (
                        <span className="selectedCountIndicator">
                            {selectedCount} {selectedLabel}
                        </span>
                    )}
                </div>
            )}




            {/* Show page indicator even for single page (1/1) */}
            {totalPages === 1 && totalCount > 0 && (
                <div className="paginationControls">
                    <div className="navigationGroup">
                        <span className="pageIndicator singlePage">
                            1 / 1
                        </span>
                    </div>
                    {selectedCount > 0 && selectedLabel && (
                        <span className="selectedCountIndicator">
                            {`${selectedCount} ${selectedLabel}`}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

ChipsPaginationWrapper.propTypes = {
    children: PropTypes.node,
    currentPage: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    isLoading: PropTypes.bool.isRequired,
    onPageChange: PropTypes.func.isRequired,
    selectedCount: PropTypes.number,
    selectedLabel: PropTypes.string,
    className: PropTypes.string,
    emptyMessage: PropTypes.string
};

export default ChipsPaginationWrapper;

